import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';

export type UserRole = 'superadmin' | 'admin' | null;

interface UseUserRoleReturn {
  role: UserRole;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  assignedMunicipalities: string[];
  loading: boolean;
}

const API_URL = import.meta.env.VITE_API_URL as string;

export const useUserRole = (): UseUserRoleReturn => {
  const { user, loading: authLoading } = useAuth();
  const [role, setRole] = useState<UserRole>(null);
  const [assignedMunicipalities, setAssignedMunicipalities] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setRole(null);
      setAssignedMunicipalities([]);
      setLoading(false);
      return;
    }

    const fetchRole = async () => {
      try {
        const res = await fetch(`${API_URL}/me`, { credentials: 'include' });
        if (!res.ok) {
          setRole(null);
          return;
        }
        const data = await res.json();
        setRole((data.role as UserRole) ?? null);
        setAssignedMunicipalities(prev => {
          const next: string[] = data.assignedMunicipalities ?? [];
          return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
        });
      } catch (err) {
        console.error('Error fetching role:', err);
        setRole(null);
      } finally {
        setLoading(false);
      }
    };

    fetchRole();
  }, [user?.id, authLoading]);

  return {
    role,
    isSuperAdmin: role === 'superadmin',
    isAdmin: role === 'admin' || role === 'superadmin',
    assignedMunicipalities,
    loading,
  };
};

